import React, { useState, useEffect } from 'react';
import { amplifyApi } from '../../services/amplifyApi';
import { useBrand } from '../../context/BrandContext';
import { User, Mail, Calendar, Wallet, CreditCard, Bell, Eye, Loader2, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';

// Default $25 minimum payout threshold (in cents) - can be overridden by API
const DEFAULT_PAYOUT_THRESHOLD = 2500;

// Helper to format cents to dollars
const formatCurrency = (cents) => {
  if (cents === undefined || cents === null) return '$0.00';
  return `$${(cents / 100).toFixed(2)}`;
};

// Helper to format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

function SettingsTab({ user }) {
  const brand = useBrand();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [commissionRates, setCommissionRates] = useState(null);
  const [error, setError] = useState(null);
  const [resetMessage, setResetMessage] = useState(null);

  // Check if referrals are enabled for this brand
  const referralsEnabled = brand.features?.referrals?.enabled !== false;

  useEffect(() => {
    loadProfile();
    loadCommissionRates();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await amplifyApi.getMyCommissions();
      if (response.success !== false) {
        setProfile(response.profile || {});
      } else {
        setError(response.error || 'Failed to load profile');
      }
    } catch (err) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const loadCommissionRates = async () => {
    try {
      const response = await amplifyApi.getCommissionRates();
      if (response.success) {
        setCommissionRates(response);
      }
    } catch (err) {
      console.error('Error loading commission rates:', err);
    }
  };

  const handleResetPublishInfo = () => {
    const storageKey = `${(brand?.name || 'store').toLowerCase().replace(/\s+/g, '_')}_hide_publish_info`;
    localStorage.removeItem(storageKey);
    setResetMessage('Publish info modal will show again next time you make a product public.');
    setTimeout(() => setResetMessage(null), 4000);
  };

  // Calculate payout eligibility - use dynamic threshold from API or default
  const payoutThreshold = commissionRates?.minPayoutAmount
    ? commissionRates.minPayoutAmount * 100  // Convert dollars to cents
    : DEFAULT_PAYOUT_THRESHOLD;
  const pendingBalance = profile?.pendingBalance || 0;
  const isEligibleForPayout = pendingBalance >= payoutThreshold;
  const amountNeeded = Math.max(payoutThreshold - pendingBalance, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-slate-600">Loading settings...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <p className="text-slate-600 mb-4">{error}</p>
        <button
          onClick={loadProfile}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <RefreshCw size={16} />
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Account Information */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">Account Information</h3>
        </div>
        <div className="p-4 space-y-4">
          {/* Email */}
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Mail size={18} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Email Address</p>
              <p className="font-medium text-slate-900">
                {user?.attributes?.email || profile?.email || 'Not available'}
              </p>
            </div>
          </div>

          {/* Username */}
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <User size={18} className="text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Username</p>
              <p className="font-medium text-slate-900">
                {user?.username || 'Not available'}
              </p>
            </div>
          </div>

          {/* Member Since */}
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <Calendar size={18} className="text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Member Since</p>
              <p className="font-medium text-slate-900">
                {formatDate(profile?.createdAt)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Payout Settings */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">Payout Settings</h3>
        </div>
        <div className="p-4 space-y-4">
          {/* Current Balance */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Wallet size={20} className="text-slate-500" />
              <div>
                <p className="text-sm text-slate-500">Pending Balance</p>
                <p className="text-xl font-bold text-slate-900">
                  {formatCurrency(pendingBalance)}
                </p>
              </div>
            </div>
            {isEligibleForPayout ? (
              <span className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                <CheckCircle size={14} />
                Eligible
              </span>
            ) : (
              <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                Need {formatCurrency(amountNeeded)} more
              </span>
            )}
          </div>

          {/* Threshold Info */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Minimum payout threshold: {formatCurrency(payoutThreshold)}</strong>
              <br />
              Once your pending balance reaches {formatCurrency(payoutThreshold)}, you'll be eligible to request a payout.
              Payouts are processed monthly.
            </p>
          </div>

          {/* Payout Method */}
          <div className="flex items-center gap-4 p-4 border border-slate-200 rounded-lg">
            <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <CreditCard size={18} className="text-slate-500" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-slate-500">Payout Method</p>
              <p className="font-medium text-slate-900">
                {profile?.payoutMethod || 'Not configured'}
              </p>
            </div>
            <button
              className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg font-medium disabled:opacity-50"
              disabled
              title="Coming soon"
            >
              Configure
            </button>
          </div>

          {/* Request Payout Button */}
          <button
            className={`w-full py-3 rounded-lg font-medium transition-colors ${
              isEligibleForPayout
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
            disabled={!isEligibleForPayout}
            title={isEligibleForPayout ? 'Request payout (coming soon)' : `Need ${formatCurrency(amountNeeded)} more to request payout`}
          >
            {isEligibleForPayout ? 'Request Payout' : `Earn ${formatCurrency(amountNeeded)} more to unlock`}
          </button>
        </div>
      </div>

      {/* Preferences */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">Preferences</h3>
        </div>
        <div className="p-4 space-y-4">
          {/* Reset Publish Info Modal */}
          <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
            <div className="flex items-center gap-3">
              <Eye size={20} className="text-slate-500" />
              <div>
                <p className="font-medium text-slate-900">Publish Info Modal</p>
                <p className="text-sm text-slate-500">
                  Show commission info when making products public
                </p>
              </div>
            </div>
            <button
              onClick={handleResetPublishInfo}
              className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg font-medium"
            >
              Reset
            </button>
          </div>

          {resetMessage && (
            <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 text-sm rounded-lg">
              <CheckCircle size={16} />
              {resetMessage}
            </div>
          )}

          {/* Email Notifications (Coming Soon) */}
          <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg opacity-60">
            <div className="flex items-center gap-3">
              <Bell size={20} className="text-slate-500" />
              <div>
                <p className="font-medium text-slate-900">Email Notifications</p>
                <p className="text-sm text-slate-500">
                  Get notified when your products sell
                </p>
              </div>
            </div>
            <span className="px-2 py-1 bg-slate-100 text-slate-500 rounded text-xs font-medium">
              Coming Soon
            </span>
          </div>
        </div>
      </div>

      {/* Commission Rates Info */}
      <div className="bg-slate-100 rounded-xl p-4">
        <h4 className="font-medium text-slate-900 mb-2">Your Commission Rates</h4>
        <div className={`grid gap-4 text-sm ${referralsEnabled ? 'grid-cols-2' : 'grid-cols-1'}`}>
          <div>
            <p className="text-slate-500">Creator Commission</p>
            <p className="font-semibold text-slate-900">
              {profile?.defaultCommissionRate ?? commissionRates?.creatorPercent ?? 10}%
            </p>
            <p className="text-xs text-slate-400 mt-1">Earned when your products sell</p>
          </div>
          {referralsEnabled && (
            <div>
              <p className="text-slate-500">Referral Commission</p>
              <p className="font-semibold text-slate-900">
                {profile?.referralCommissionRate ?? commissionRates?.referralPercent ?? 5}%
              </p>
              <p className="text-xs text-slate-400 mt-1">Earned when you share others' products</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SettingsTab;
